
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from '@klv/hooks/useTranslation';

import type { IGatsbyImageData } from 'gatsby-plugin-image';
import * as s from './styles';
import type { ISocialCard } from './types';

const SocialCard = ({ 
  swipe, 
  $current, 
  $index, 
  $activeIndex,
  slidePlaying,
  setSlidePlaying,
  setThisSlide,
  attributionCompany,
  attributionJobTitle,
  attributionName,
  videoPoster, 
  videoUrl, 
  videoDuration, 
  quote, 
  tag, 
  logo, 
}: ISocialCard) => {
  const { t } = useTranslation();

  const [hydrated, setHydrated] = useState(false);
  const videoPlayerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setHydrated(true);
  }, []);

  const clickHandler = () => {
    /*
    On a desktop, any video should be playable at any time.
    On mobile only the currently active video can be played
    */
    if ($current !== undefined || window.matchMedia('(min-width: 1024px)').matches) {
      setSlidePlaying($index);
      setThisSlide($index);
    } else {
      /*
      If we're on mobile and made it here, it means we clicked a video that is not on
      the current active card. In this case, trigger the correct swipe direction.
      */
      if ($index < $activeIndex) {
        swipe(-1);
      } else {
        swipe(1);
      }
      // On swipe, reset slidePlaying to none.
      setSlidePlaying(-1);
    }
  };

  const handleKeyUp = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (
      event.code === 'Space'
      || event.code === 'Enter'
    ) {
      event.preventDefault();
      clickHandler();
      videoPlayerRef?.current?.focus?.();
    }
  };

  return (
    <s.Card onClick={clickHandler} onKeyUp={handleKeyUp} onFocus={() => {setThisSlide($index);}} data-social-card tabIndex={0}>
      <s.VideoContainer isPlaying={slidePlaying === $index}> 
        <s.VideoBlur isPlaying={slidePlaying === $index}>
          {logo && <s.Logo imageData={logo?.gatsbyImage as IGatsbyImageData} altText={logo?.altText}/>}
          <s.PlayButtonWrapper>
            <s.PlayButtonCanvas>
              <s.PlayButton>
                <svg width="13" height="16" viewBox="0 0 13 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12.5 7.13398C13.1667 7.51888 13.1667 8.48113 12.5 8.86603L2 14.9282C1.33333 15.3131 0.499999 14.832 0.499999 14.0622L0.5 1.93782C0.5 1.16802 1.33333 0.686896 2 1.0718L12.5 7.13398Z" fill="white"/>
                </svg>
              </s.PlayButton>
              {videoDuration && <s.PlayButtonTimer>{videoDuration}</s.PlayButtonTimer>}
            </s.PlayButtonCanvas>
          </s.PlayButtonWrapper>
        </s.VideoBlur>
        {hydrated && ( <s.StyledVimeoPlayer
          aria-label={videoPoster?.altText}
          config={{
            vimeo: {
              playerOptions: {
                controls: true,
                responsive: true,
                autopause: true,
                texttrack: t('langCodes_recaptchaLanguage') === 'en' ? '' : t('langCodes_recaptchaLanguage'),
              },
            },
          }}
          onEnded={() => {setSlidePlaying(-1);}}
          playing={slidePlaying === $index}
          url={videoUrl}
          height="100%"
          width="100%"
          ref={videoPlayerRef}
          style={{ display:slidePlaying === $index ? 'block' : 'none' }}
        />)}
        <s.VideoPoster imageData={videoPoster?.gatsbyImage as IGatsbyImageData} altText={videoPoster?.altText}/>
      </s.VideoContainer>
      <s.CardContents>
      {quote && <s.Quote>"{quote}"</s.Quote>}
      {(attributionCompany || attributionJobTitle || attributionName ) &&  (
        <s.AttributionWrapper>
          {attributionCompany && <s.Attribution>{attributionCompany}</s.Attribution>}
          {attributionJobTitle && <s.Attribution>{attributionJobTitle}</s.Attribution>}
          {attributionName && <s.Attribution>{attributionName}</s.Attribution>}
        </s.AttributionWrapper>
      )}
        {tag && (
          <s.TagWrapper>
            <s.Tag>{tag}</s.Tag>
          </s.TagWrapper>
        )}
      </s.CardContents>
    </s.Card>
  );
};

export default SocialCard;
